import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Fixed design canvas — every slide is authored at this size, then scaled to fit.
const DESIGN_W = 1600;
const DESIGN_H = 900;

// When set (by the Presenter View), Stage renders a scaled block instead of
// filling the window — used to show live slide thumbnails.
export const StagePreviewContext = React.createContext(null);

// Orchestrated entrance: staggered rise + fade. Remounts per slide via key.
export const useStageMotion = () => {
  const reduce = useReducedMotion();
  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: reduce ? 0 : 0.11, delayChildren: 0.06 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  return { container, item, reduce };
};

// Desktop: render slide on a fixed 1600x900 canvas scaled to fit (everything
// always in view, no scroll). Mobile (<900px): reflow + scroll.
export const Stage = ({ children, testId }) => {
  const preview = React.useContext(StagePreviewContext);
  const [mode, setMode] = useState("scaled");
  const [scale, setScale] = useState(1);
  const [scroll, setScroll] = useState(false);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w < 900) {
        setMode("flow");
        return;
      }
      setMode("scaled");
      const availW = w - 88; // side breathing room
      const availH = h - 120; // top/bottom chrome
      const wS = availW / DESIGN_W;
      const hS = availH / DESIGN_H;
      const MAX = 1.15;
      const READABLE = 0.9; // below this the deck feels cramped/tiny
      const fit = Math.min(wS, hS, MAX);
      // If everything fits at a comfortable size, use that (centered, no scroll).
      // Otherwise fill the width for readability and allow a little vertical scroll.
      const s = fit >= READABLE ? fit : Math.min(wS, MAX);
      setScale(s);
      setScroll(DESIGN_H * s > availH + 1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Let the deck's edge fades show while the stage scrolls (keeps content from
  // colliding with the fixed top/bottom chrome).
  useEffect(() => {
    if (preview) return;
    document.body.classList.toggle("deck-scrolling", scroll || mode === "flow");
    return () => document.body.classList.remove("deck-scrolling");
  }, [scroll, mode, preview]);

  // Presenter thumbnail: scaled block sized to its container.
  if (preview) {
    return (
      <div
        style={{
          width: `${DESIGN_W * preview.scale}px`,
          height: `${DESIGN_H * preview.scale}px`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          className="stage-fixed"
          data-testid={testId}
          style={{
            width: `${DESIGN_W}px`,
            height: `${DESIGN_H}px`,
            transform: `scale(${preview.scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  if (mode === "flow") {
    return (
      <div className="stage-wrap">
        <div className="stage" data-testid={testId}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`stage-viewport${scroll ? " stage-viewport-scroll" : ""}`}>
      <div
        style={{
          width: `${DESIGN_W * scale}px`,
          height: `${DESIGN_H * scale}px`,
          position: "relative",
          overflow: "hidden",
          flex: "none",
        }}
      >
        <div
          className="stage-fixed"
          data-testid={testId}
          style={{
            width: `${DESIGN_W}px`,
            height: `${DESIGN_H}px`,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const Reveal = motion.div;

export default Stage;
